import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Flex, Modal } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import Grade from '@totem/components/common/Grade';
import Tooltip from '@totem/components/common/tooltip/Tooltip';
import RegionsContext from '@totem/components/portfolio/regions/list/RegionsContext';
import { RegionSummaryItem } from '@totem/components/portfolio/regions/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';

const RegionsTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { data, loading, onAction } = useContext(RegionsContext);

  const canManage =
    permissions.organizationalAdministrator.hasPermissions(userProfile);

  const columns: ColumnProps<RegionSummaryItem>[] = [
    {
      title: 'Region Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (recA, recB) => recA.name.localeCompare(recB.name),
      render: (_, record) => (
        <Link to={`/dashboard/regions/details/${record.id}`}>
          {record.name}
        </Link>
      ),
    },
    {
      title: 'Buildings',
      dataIndex: 'buildings',
      key: 'buildings',
      width: 150,
      sorter: (recA, recB) => recA.buildings - recB.buildings,
    },
    {
      title: 'Control Systems',
      dataIndex: 'controlSystems',
      key: 'controlSystems',
      width: 150,
      sorter: (recA, recB) => recA.controlSystems - recB.controlSystems,
    },
    {
      title: 'Score',
      key: 'policyAudits',
      width: 100,
      align: 'center',
      render: (_, record) =>
        isNotNull(record.policyAudits) &&
        record.policyAudits.count > 0 &&
        record.policyAudits.maxScore > 0 && (
          <Grade
            score={
              (record.policyAudits.totalScore / record.policyAudits.maxScore) *
              100
            }
            size="small"
            style={{ margin: '0 auto' }}
          />
        ),
    },
    ...(canManage
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 90,
            align: 'center',
            render: (_: unknown, record: RegionSummaryItem) => (
              <Flex justify={'flex-end'} align={'center'}>
                <div>
                  <Tooltip title="Edit" placement="top">
                    <Button
                      type="text"
                      shape="circle"
                      icon={<EditOutlined />}
                      onClick={() => onAction('edit', record)}
                    />
                  </Tooltip>
                </div>
                <div>
                  <Tooltip title="Delete" placement="top">
                    <Button
                      type="text"
                      danger
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        Modal.confirm({
                          title: 'Delete this region?',
                          content: `Are you sure you want to delete the region "${record.name.trim()}"?`,
                          okText: 'Yes, Delete',
                          okType: 'danger',
                          cancelText: 'No',
                          onOk: () => onAction('delete', record),
                        })
                      }
                    />
                  </Tooltip>
                </div>
              </Flex>
            ),
          } as ColumnProps<RegionSummaryItem>,
        ]
      : []),
  ];

  return (
    <IBTable
      rowKey="id"
      columns={columns}
      loading={loading}
      dataSource={isNotNull(data) ? data : []}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
    />
  );
};

export default RegionsTable;
